import { useCallback, useEffect, useMemo } from 'react'
import { AxiosError } from 'axios'
import qs from 'qs'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { useDispatch } from '@tabeeb/store'
import { onAddErrorNotification, onAddSuccessNotification } from '@tabeeb/modules/notification/actions'
import { ApiErrorResponse, UserResponse } from '@tabeeb/contracts/main-api/swagger-main-api'
import type { Api as CertificateServiceApi } from '@tabeeb/contracts/microservices/swagger-certificate-service'
import getErrorMessageFromResponse from '@tabeeb/modules/shared/utils/getErrorMessageFromResponse'
import { useApi } from '@tabeeb/modules/shared/utils/API/hooks'

import { CertificatesQueryKeys } from '../queries'
import type { CertificateType } from '../types'

type GetAllWithPaginationParams = NonNullable<
  Parameters<CertificateServiceApi<unknown>['certificateTypes']['getAllWithPaginationAsync']>[0]
>

export interface UseCertificatesTypesProps {
  search?: GetAllWithPaginationParams['Search']
  sorting?: {
    field: GetAllWithPaginationParams['Sort.Field']
    order: GetAllWithPaginationParams['Sort.SortOrder']
  }
  filtering?: GetAllWithPaginationParams['FilterModels']
  pageNumber: GetAllWithPaginationParams['PageNumber']
  pageSize: GetAllWithPaginationParams['PageSize']
}

const useCertificatesTypes = ({ search, sorting, pageNumber, pageSize, filtering }: UseCertificatesTypesProps) => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()

  const { certificateServiceApi: api, mainApi } = useApi()

  const typesQuery = useQuery({
    queryKey: [
      CertificatesQueryKeys.CertificateTypes,
      search,
      sorting?.field,
      sorting?.order,
      pageNumber,
      pageSize,
      filtering,
    ],
    queryFn: async () => {
      const typesResponse = await api.certificateTypes.getAllWithPaginationAsync(
        {
          Search: search,
          'Sort.Field': sorting?.field,
          'Sort.SortOrder': sorting?.order,
          PageNumber: pageNumber,
          PageSize: pageSize,
          FilterModels: filtering,
        },
        {
          paramsSerializer: (params) => qs.stringify(params, { allowDots: true }),
        }
      )

      const ownerGuids = new Set<string>()
      for (const certificateType of typesResponse.CertificateTypes) {
        if (certificateType.CreatedBy) {
          ownerGuids.add(certificateType.CreatedBy)
        }
      }

      let owners: UserResponse[] = []
      if (ownerGuids.size > 0) {
        const ownersResponse = await mainApi.users.getUsersByIdentityGuidsAsync(
          {
            IdentityGuids: Array.from(ownerGuids),
          },
          {
            paramsSerializer: (params) => qs.stringify(params, { allowDots: true }),
          }
        )
        owners = ownersResponse ?? []
      }

      const typesWithOwners = typesResponse.CertificateTypes.map((type) => ({
        ...type,
        Owner: owners.find((u) => u.IdentityGuid === type.CreatedBy),
      }))

      return {
        CertificateTypes: typesWithOwners as CertificateType[],
        TotalCount: typesResponse.TotalCount,
      }
    },
  })

  const { isError: isTypesError, error: typesError } = typesQuery

  useEffect(() => {
    if (isTypesError && typesError) {
      const error = typesError as AxiosError<ApiErrorResponse>
      const message = getErrorMessageFromResponse(error.response?.data)
      dispatch(onAddErrorNotification({ message }))
    }
  }, [isTypesError, typesError, dispatch])

  const deleteMutation = useMutation({
    mutationFn: api.certificateTypes.deleteAsync,
    onError: () => {
      dispatch(onAddErrorNotification({ message: 'Failed to delete certificate type' }))
    },
    onSuccess: () => {
      dispatch(onAddSuccessNotification({ message: 'Certificate type deleted successfully' }))
      queryClient.invalidateQueries({ queryKey: [CertificatesQueryKeys.CertificateTypes] })
    },
  })

  const updateMutation = useMutation({
    mutationFn: api.certificateTypes.updateAsync,
    onError: () => {
      dispatch(onAddErrorNotification({ message: 'Failed to update certificate type' }))
    },
    onSuccess: () => {
      dispatch(onAddSuccessNotification({ message: 'Certificate type updated successfully' }))
      queryClient.invalidateQueries({ queryKey: [CertificatesQueryKeys.CertificateTypes] })
    },
  })

  const createMutation = useMutation({
    mutationFn: api.certificateTypes.createAsync,
    onError: () => {
      dispatch(onAddErrorNotification({ message: 'Failed to create certificate type' }))
    },
    onSuccess: () => {
      dispatch(onAddSuccessNotification({ message: 'Certificate type created successfully' }))
      queryClient.invalidateQueries({ queryKey: [CertificatesQueryKeys.CertificateTypes] })
    },
  })

  const restoreMutation = useMutation({
    mutationFn: api.certificateTypes.restoreAsync,
    onError: () => {
      dispatch(onAddErrorNotification({ message: 'Failed to restore certificate type' }))
    },
    onSuccess: () => {
      dispatch(onAddSuccessNotification({ message: 'Certificate type restored successfully' }))
      queryClient.invalidateQueries({ queryKey: [CertificatesQueryKeys.CertificateTypes] })
    },
  })

  const deletePermanentlyMutation = useMutation({
    mutationFn: api.certificateTypes.deletePermanentlyAsync,
    onError: () => {
      dispatch(onAddErrorNotification({ message: 'Failed to permanently delete certificate type' }))
    },
    onSuccess: () => {
      dispatch(onAddSuccessNotification({ message: 'Certificate type permanently deleted' }))
      queryClient.invalidateQueries({ queryKey: [CertificatesQueryKeys.CertificateTypes] })
    },
  })

  const onReload = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [CertificatesQueryKeys.CertificateTypes] })
  }, [queryClient])

  const loading = useMemo(
    () =>
      typesQuery.isLoading ||
      typesQuery.isFetching ||
      deleteMutation.isPending ||
      updateMutation.isPending ||
      createMutation.isPending ||
      restoreMutation.isPending ||
      deletePermanentlyMutation.isPending,
    [
      typesQuery.isLoading,
      typesQuery.isFetching,
      deleteMutation.isPending,
      updateMutation.isPending,
      createMutation.isPending,
      restoreMutation.isPending,
      deletePermanentlyMutation.isPending,
    ]
  )

  return useMemo(
    () => ({
      certificateTypes: typesQuery.data?.CertificateTypes ?? [],
      totalCount: typesQuery.data?.TotalCount ?? 0,
      onDeleteAsync: deleteMutation.mutateAsync,
      onDeletePermanentlyAsync: deletePermanentlyMutation.mutateAsync,
      onUpdateAsync: updateMutation.mutateAsync,
      onCreateAsync: createMutation.mutateAsync,
      onRestoreAsync: restoreMutation.mutateAsync,
      onReload,
      loading,
    }),
    [
      typesQuery.data,
      deleteMutation.mutateAsync,
      deletePermanentlyMutation.mutateAsync,
      updateMutation.mutateAsync,
      createMutation.mutateAsync,
      restoreMutation.mutateAsync,
      onReload,
      loading,
    ]
  )
}

export default useCertificatesTypes
